<template>
  <div class="flex flex-col proxima_nova text_color gap-10">
    <div class="flex flex-row justify-between">
      <div class="flex flex-col">
        <h1 class="text-xl font-semibold">Sender Domains</h1>
        <span class="secondary_color text-xs"
          >Sender domain is where you send your emails from—the part that comes
          after @ in your email address. Adding your own domain decreases the
          chances of your campaigns and automated emails ending up in a spam
          folder.
        </span>
      </div>
      <a-button class="primary_medium" @click="addDomain">Add Domain</a-button>
    </div>
    <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-2 mt-20"
      >
      <SkeletonLoader v-if="loading" :page="loaderPages.TABLE" />
      </div>
      <div v-else-if="senderDomainsData.length === 0">
        <EmptyScreen
          source="sender-domains-empty"
          text="You Haven't Added Any Sender Domains Yet!"
        />
      </div>
    <div v-else>
      <CustomTable
        :headers="senderDomainHeader"
        :data="senderDomainsData"
        height="calc(100vh - (270px + var(--banner-height)))"
        :show-actions-column="true"
        :actions="actionOptions"
        :custom-row="customRow"
      />
    </div>

    <CustomModal
      v-model:visible="isModalVisible"
      title="Add Domain Details"
      width="36vw"
    >
      <CustomAlerts
        source="domain"
        type="info"
        title="Benefits of using a custom sender domain"
        customColor="rgba(24, 144, 255, 0.60)"
      >
        <p class="text-sm font-normal text_color">
          Using your own domain increases the chances of your emails
          successfully landing in the recipient's inbox. This also entirely
          removes the "via cntl.com" message displayed beside your sender email
          address. Your sender email address must match your domain (e.g.
          domain.com if your sender email is az@domain.com)else your emails can
          bounce/fail.
        </p>
      </CustomAlerts>

      <div class="mt-6">
        <label class="secondary_color text-[14px]"
          >Enter your sending domain</label
        >
        <a-input
          v-model:value="sendingDomain"
          placeholder="Enter your sending domain"
          class="input_medium mt-1"
        />
      </div>

      <template #footer>
        <a-button
          class="primary_medium"
          @click="proceedWithDomain"
          :loading="loading"
          >Proceed</a-button
        >
      </template>
    </CustomModal>

    <CustomModal
      v-model:visible="isDeleteModalVisible"
      title="Delete Domain"
      width="30vw"
    >
      <p class="text-sm font-normal text_color">
        Are you sure you want to delete this domain?
      </p>
      <template #footer>
        <div class="flex gap-2">
          <a-button
            @click="isDeleteModalVisible = false"
            class="secondary_medium"
            >Cancel</a-button
          >
          <a-button
            class="primary_medium"
            @click="confirmDelete"
            :loading="loading"
            >Delete</a-button
          >
        </div>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { senderDomainHeader } from "@/src/constants/campaignInEngageConstants.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import { ref, onMounted } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomAlerts from "@/src/components/CustomAlerts.vue";
import { useRouter } from "vue-router";
import SuperCampaignsService from "@/src/api/SuperCampaignsService.js";
import { message } from "ant-design-vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const loading = ref(true);
const senderDomainsData = ref([]);

const isModalVisible = ref(false);
const isDeleteModalVisible = ref(false);
const selectedDomain = ref(null);
const sendingDomain = ref("");
const router = useRouter();

const fetchSenderDomains = async () => {
  try {
    loading.value = true;
    const response = await SuperCampaignsService.fetchEmailDomains();
    if (response?.success) {
      senderDomainsData.value = (response.domains || []).map((domain) => ({
        ...domain,
        status: domain.status.charAt(0).toUpperCase() + domain.status.slice(1),
      }));
    } else {
      message.error(response?.message || "Failed to fetch sender domains");
    }
  } catch (error) {
    message.error("An error occurred while fetching domains");
    console.error("Error while fetching sender domains: ", error);
  } finally {
    loading.value = false;
  }
};

const addDomain = () => {
  sendingDomain.value = "";
  isModalVisible.value = true;
};

const showDeleteModal = (row) => {
  selectedDomain.value = row;
  isDeleteModalVisible.value = true;
};

const proceedWithDomain = async () => {
  try {
    loading.value = true;
    const payload = {
      domain: sendingDomain.value,
      sending_domain: sendingDomain.value,
    };
    const response = await SuperCampaignsService.createEmailDomain(payload);
    if (response?.success) {
      message.success("Sender Domain Added Successfully");
      await router.push({
        path: `/settings/email_campaign_settings/sender-domains/update`,
        query: { sendingDomain: sendingDomain.value },
      });
    } else if (response?.message) {
      message.error(response?.message);
    } else {
      message.error("Failed to add sender domain");
    }
  } catch (error) {
    message.error("An error occurred while adding domain");
    console.error("Error while adding sender domain: ", error);
  } finally {
    loading.value = false;
    isModalVisible.value = false;
  }
};

const confirmDelete = async () => {
  if (selectedDomain.value) {
    try {
      loading.value = true;
      const payload = {
        sending_domain: selectedDomain.value.sending_domain,
      };
      console.log("selected domain", selectedDomain.value.sending_domain);
      const response = await SuperCampaignsService.deleteEmailDomain(payload);
      if (response?.success) {
        message.success(response?.message || "Domain deleted successfully");
        await fetchSenderDomains();
      } else {
        message.error(response?.message || "Failed to delete domain");
      }
    } catch (error) {
      message.error("An error occurred while deleting domain");
      console.error("Error while deleting sender domain: ", error);
    } finally {
      loading.value = false;
      isDeleteModalVisible.value = false;
      selectedDomain.value = null;
    }
  }
};

const actionOptions = [
  {
    label: "Delete",
    handler: (row) => showDeleteModal(row),
    icon: imageNames.DELETE_GREY,
  },
];

const customRow = (record) => {
  return {
    onClick: () => {
      router.push({
        path: `/settings/email_campaign_settings/sender-domains/update`,
        query: { sendingDomain: record.sending_domain },
      });
    },
    "data-href": router.resolve({
      path: `/settings/email_campaign_settings/sender-domains/update`,
      query: { sendingDomain: record.sending_domain },
    }).href,
  };
};

onMounted(async () => {
  await fetchSenderDomains();
});
</script>
